/**
 * Map layers configuration.
 *
 * Layers are organized by availability:
 * - global: Available on all city maps
 * - city-specific: Only available on specific city subdomains
 *
 * Each layer config includes:
 * - id: Unique identifier for the layer
 * - label: Display name in the UI
 * - type: 'geojson' | 'vector' | 'raster'
 * - source: URL or path to the data
 * - style: Mapbox layer style configuration
 * - defaultVisible: Whether layer is visible by default
 */

// Base path for local GeoJSON files (in Drupal public files directory)
const DATA_BASE_PATH = '/sites/default/files/map-layers';

/**
 * Global layers available on all city maps
 */
export const globalLayers = [
  // Add global layers here as needed
  // Example:
  // {
  //   id: 'opportunity-zones',
  //   label: 'Opportunity Zones',
  //   type: 'geojson',
  //   source: `${DATA_BASE_PATH}/opportunity-zones.geojson`,
  //   style: { ... },
  //   defaultVisible: false,
  // },
];

/**
 * City-specific layers
 * Keys are domain IDs (e.g., 'la_urbanize_city')
 */
export const cityLayers = {
  // Los Angeles
  la_urbanize_city: [
    {
      id: 'la-council-districts',
      label: 'LA City Council Districts',
      type: 'geojson',
      source: `${DATA_BASE_PATH}/la-council-districts.geojson?v=2`,
      style: {
        type: 'line',
        paint: {
          'line-color': '#374151',
          'line-width': 1.5,
          'line-opacity': 1,
        },
      },
      fillStyle: {
        type: 'fill',
        paint: {
          // Color-code by district number
          'fill-color': [
            'match',
            ['get', 'DISTNO'],
            1, '#ef4444',   // red
            2, '#f97316',   // orange
            3, '#eab308',   // yellow
            4, '#22c55e',   // green
            5, '#14b8a6',   // teal
            6, '#06b6d4',   // cyan
            7, '#3b82f6',   // blue
            8, '#8b5cf6',   // violet
            9, '#d946ef',   // fuchsia
            10, '#ec4899',  // pink
            11, '#f43f5e',  // rose
            12, '#78716c',  // stone
            13, '#64748b',  // slate
            14, '#0891b2',  // cyan-600
            15, '#059669',  // emerald
            '#6B7280'       // default gray
          ],
          'fill-opacity': 0.5,
        },
      },
      labelStyle: {
        type: 'symbol',
        layout: {
          'text-field': ['concat', 'District ', ['to-string', ['get', 'DISTNO']]],
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 14,
          'text-anchor': 'center',
          'text-allow-overlap': false,
          'text-ignore-placement': false,
        },
        paint: {
          'text-color': '#1f2937',
          'text-halo-color': '#ffffff',
          'text-halo-width': 2,
        },
      },
      defaultVisible: false,
    },
  ],

  // Chicago - add layers here
  chicago_urbanize_city: [],

  // Atlanta - add layers here
  atlanta_urbanize_city: [],

  // New York - add layers here
  nyc_urbanize_city: [],

  // San Francisco - add layers here
  sf_urbanize_city: [],

  // Add other cities as needed...
};

/**
 * Get all available layers for a given domain
 * @param {string} domainId - The current domain ID
 * @returns {Array} - Combined array of global and city-specific layers
 */
export function getAvailableLayers(domainId) {
  const citySpecificLayers = cityLayers[domainId] || [];
  return [...globalLayers, ...citySpecificLayers];
}

/**
 * Get initial layer visibility state
 * @param {string} domainId - The current domain ID
 * @returns {Object} - Map of layer ID to visibility boolean
 */
export function getInitialLayerState(domainId) {
  const layers = getAvailableLayers(domainId);
  const state = {};
  layers.forEach((layer) => {
    state[layer.id] = layer.defaultVisible || false;
  });
  return state;
}

